import { AxleEvent } from '@/components/calendars/AxleCalendar';
import { AXLE_TEAL } from '@/constants/colors';
import { DropReason } from '@/types/shifts';
import { formatTimeRange } from '@/utils/datetimes/formatting';
import { gql } from '@apollo/client';
import moment from 'moment';
import { CSSProperties } from 'react';
import { EventPropGetter } from 'react-big-calendar';

/*******************
 * Shifts
 ******************/
export const ScheduleShiftFragment = gql`
    fragment ScheduleShiftFragment on ShiftOccurrence {
        id
        startDatetime
        endDatetime
        clinician {
            id
            timezone
        }
        isCanceled
        recurrence {
            id
            dropReason
            serviceLimits {
                id
                service {
                    id
                }
                maxLimit
            }
        }
    }
`;

export interface IShiftScheduleEvent {
    id: string;
    startDatetime: Date;
    endDatetime: Date;
    clinician: {
        id: string;
        timezone: string | null;
    };
    isCanceled: boolean;
    recurrence: {
        id: string;
        dropReason: DropReason | null;
        serviceLimits: {
            id: string;
            service: {
                id: string;
            };
            maxLimit: number;
        }[];
    };
}

type ShiftCalendarEvent = AxleEvent & {
    type: CalendarEventType.SHIFT;
    event: IShiftScheduleEvent;
};

/*********************
 * Shift blocks
 *********************/
export const ScheduleShiftBlockFragment = gql`
    fragment ScheduleShiftBlockFragment on ShiftBlockOccurrence {
        id
        startDatetime
        endDatetime
        clinician {
            id
            firstName
            lastName
        }
        recurrence {
            id
            notes
            numAllClinicians
        }
    }
`;

export interface IShiftBlockScheduleEvent {
    id: string;
    startDatetime: Date;
    endDatetime: Date;
    recurrence: {
        id: string;
        notes: string;
        numAllClinicians: number;
    };
    clinician: {
        id: string;
        firstName: string;
        lastName: string;
    } | null;
}

type ShiftBlockCalendarEvent = AxleEvent & {
    type: CalendarEventType.SHIFT_BLOCK;
    event: IShiftBlockScheduleEvent;
};

export const getShiftBlockOccurrenceLabel = (shiftBlock: IShiftBlockScheduleEvent) => {
    return shiftBlock.recurrence.notes === ''
        ? shiftBlock.recurrence.numAllClinicians === 1
            ? `Shift Block for ${shiftBlock.clinician?.firstName ?? ''} ${
                  shiftBlock.clinician?.lastName ?? ''
              }`.trim()
            : 'Shift Block for multiple clinicians'
        : shiftBlock.recurrence.notes;
};

/*************************
 * Tentative shift blocks
 ************************/
export const ScheduleTentativeShiftBlockFragment = gql`
    fragment ScheduleTentativeShiftBlockFragment on TentativeShiftBlockOccurrence {
        id
        startDatetime
        endDatetime
        parentRequest {
            id
            requestingClinician {
                id
            }
            notes
        }
    }
`;

export interface ITentativeShiftBlockScheduleEvent {
    id: string;
    startDatetime: Date;
    endDatetime: Date;
    parentRequest: {
        id: string;
        requestingClinician: {
            id: string;
        };
        notes: string;
    };
}

type TentativeShiftBlockCalendarEvent = AxleEvent & {
    type: CalendarEventType.TENTATIVE_SHIFT_BLOCK;
    event: ITentativeShiftBlockScheduleEvent;
};

/*************************
 * Clinician appointments
 *************************/
export const ScheduleClinicianAppointmentFragment = gql`
    fragment ScheduleClinicianAppointmentFragment on ClinicianAppointment {
        id
        startDatetime
        endDatetime
        clinician {
            id
        }
        visit {
            id
            patient {
                id
                fullName
            }
        }
        encounters {
            id
            service {
                id
                name
            }
        }
    }
`;

export interface IClinicianAppointmentScheduleEvent {
    id: string;
    startDatetime: Date | null;
    endDatetime: Date | null;
    visit: {
        id: string;
        patient: {
            id: string;
            fullName: string;
        };
    } | null;
    encounters: {
        id: string;
        service: {
            id: string;
            name: string;
        };
    }[];
    clinician: {
        id: string;
    } | null;
}

type ClinicianAppointmentCalendarEvent = AxleEvent & {
    type: CalendarEventType.CLINICIAN_APPOINTMENT;
    event: IClinicianAppointmentScheduleEvent;
};

/*************************
 * Clinician Reservations
 *************************/
export const ScheduleClinicianReservationFragment = gql`
    fragment ScheduleClinicianReservationFragment on ClinicianReservation {
        id
        clinician {
            id
            firstName
            lastName
        }
        startOffsetMinutes
        durationMinutes
        isVirtual
        startDatetime
        endDatetime
        visitReservation {
            id
            organization {
                id
            }
            externalId
            startDatetime
            expiryDatetime
            address {
                id
                address1
                address2
                city
                state
                zipcode
            }
            zipCode
            isFulfilled
        }
    }
`;

export interface IClinicianReservationScheduleEvent {
    id: string;
    clinician: {
        id: string;
        firstName: string;
        lastName: string;
    };
    startOffsetMinutes: number;
    durationMinutes: number;
    isVirtual: boolean;
    startDatetime: Date;
    endDatetime: Date;
    visitReservation: {
        id: string;
        organization: {
            id: string;
        };
        externalId: string | null;
        startDatetime: Date;
        expiryDatetime: Date;
        address: {
            id: string;
            address1: string;
            address2: string | null;
            city: string;
            state: string;
            zipcode: string;
        } | null;
        zipCode: string | null;
        isFulfilled: boolean;
    };
}

type ClinicianReservationCalendarEvent = AxleEvent & {
    type: CalendarEventType.CLINICIAN_RESERVATION;
    event: IClinicianReservationScheduleEvent;
};

/*******************
 * Unavailability
 ******************/
export const TentativeClinicianAppointmentFragment = gql`
    fragment TentativeClinicianAppointmentFragment on ClinicianAppointment {
        id
        durationMinutes
        startOffsetMinutes
        visit {
            id
            patient {
                id
                fullName
            }
        }
        encounters {
            id
            service {
                id
                name
            }
        }
    }
`;

export interface ITentativeClinicianAppointmentScheduleEvent {
    id: string;
    durationMinutes: number;
    startOffsetMinutes: number;
    visit: {
        id: string;
        patient: {
            id: string;
            fullName: string;
        };
    };
    encounters: {
        id: string;
        service: {
            id: string;
            name: string;
        };
    }[];
}

export type TentativeClinicianAppointmentCalendarEvent = AxleEvent & {
    type: CalendarEventType.TENTATIVE_CLINICIAN_APPOINTMENT;
    event: ITentativeClinicianAppointmentScheduleEvent;
};

/*******************
 * Unavailability
 ******************/
export type UnavailabilityCalendarEvent = AxleEvent & {
    type: CalendarEventType.UNAVAILABILITY;
    event: {
        id: string;
        startDatetime: Date;
        endDatetime: Date;
        resourceId: string;
    };
};

/*******************
 * Event Clinician
 ******************/
export const ScheduleClinicianFragment = gql`
    fragment ScheduleClinicianFragment on Clinician {
        id
        firstName
        lastName
        timezone
        logoUrl
    }
`;

export interface IScheduleClinician {
    id: string;
    firstName: string;
    lastName: string;
    timezone: string;
    logoUrl: string | null;
}

/*******************
 * Unscheduled Visits
 ******************/
export const ScheduleUnscheduledVisitFragment = gql`
    fragment ScheduleUnscheduledVisitFragment on Visit {
        id
        effectiveDate
        clinicianAppointments {
            id
            clinician {
                id
                fullName
            }
            services {
                id
                name
            }
            visit {
                id
                patient {
                    id
                    fullName
                }
            }
        }
    }
`;

export interface IScheduleUnscheduledAppointment {
    id: string;
    clinician: {
        id: string;
        fullName: string;
    } | null;
    services: {
        id: string;
        name: string;
    }[];
    visit: {
        id: string;
        patient: {
            id: string;
            fullName: string;
        };
    };
}

export interface IScheduleUnscheduledVisit {
    id: string;
    effectiveDate: Date;
    clinicianAppointments: IScheduleUnscheduledAppointment[];
}

/*******************
 * Visit Requests
 ******************/
export const ScheduleVisitRequestFragment = gql`
    fragment ScheduleVisitRequestFragment on VisitRequest {
        id
        effectiveDate
        appointments {
            id
            clinician {
                id
                fullName
            }
            services {
                id
                name
            }
            visitRequest {
                id
                patient {
                    id
                    fullName
                }
            }
        }
    }
`;

export interface IScheduleVisitRequestAppointment {
    id: string;
    clinician: {
        id: string;
        fullName: string;
    } | null;
    services: {
        id: string;
        name: string;
    }[];
    visitRequest: {
        id: string;
        patient: {
            id: string;
            fullName: string;
        };
    };
}

export interface IScheduleVisitRequest {
    id: string;
    effectiveDate: Date;
    appointments: IScheduleVisitRequestAppointment[];
}

/*******************
 * Common
 ******************/
export type CalendarEvent = AxleEvent &
    (
        | ShiftCalendarEvent
        | ShiftBlockCalendarEvent
        | TentativeShiftBlockCalendarEvent
        | ClinicianAppointmentCalendarEvent
        | ClinicianReservationCalendarEvent
        | TentativeClinicianAppointmentCalendarEvent
        | UnavailabilityCalendarEvent
    );

export enum CalendarEventType {
    SHIFT = 'SHIFT',
    SHIFT_BLOCK = 'SHIFT_BLOCK',
    TENTATIVE_SHIFT_BLOCK = 'TENTATIVE_SHIFT_BLOCK',
    CLINICIAN_APPOINTMENT = 'CLINICIAN_APPOINTMENT',
    CLINICIAN_RESERVATION = 'CLINICIAN_RESERVATION',
    UNAVAILABILITY = 'UNAVAILABILITY',
    TENTATIVE_CLINICIAN_APPOINTMENT = 'TENTATIVE_CLINICIAN_APPOINTMENT',
}

const unavailableBlockGradient = `
    repeating-linear-gradient(45deg,
        #666 10px,
        #666 12px,
        #111 12px,
        #111 21px
    )
`;

export const multiClinicianSchedulesEventPropGetter: EventPropGetter<CalendarEvent> = ({
    type,
    event,
}: CalendarEvent) => {
    const style: CSSProperties = {
        borderRadius: '5px',
        color: 'white',
        border: '0px',
        fontSize: '11px',
    };
    switch (type) {
        case CalendarEventType.SHIFT:
            // Check if shift is cancelled OR has a dropReason of BLOCKED or DROPPED
            if (
                event.isCanceled ||
                event.recurrence.dropReason === DropReason.BLOCKED ||
                event.recurrence.dropReason === DropReason.DROPPED
            ) {
                style.backgroundImage = unavailableBlockGradient;
            } else {
                style.backgroundColor = AXLE_TEAL;
            }
            break;
        case CalendarEventType.SHIFT_BLOCK:
            style.backgroundImage = unavailableBlockGradient;
            // Make text more readable against the background
            style.textShadow =
                '0 0 1px rgba(0,0,0,0.5), 0 0 2px rgba(0,0,0,0.5), 1px 1px 2px rgba(0,0,0,0.5)';
            break;
        case CalendarEventType.TENTATIVE_SHIFT_BLOCK:
            // TODO(jessica): Revisit this styling.
            style.backgroundImage = unavailableBlockGradient;
            style.border = '2px dashed #666';
            // Make text more readable against the background
            style.textShadow =
                '0 0 1px rgba(0,0,0,0.5), 0 0 2px rgba(0,0,0,0.5), 1px 1px 2px rgba(0,0,0,0.5)';
            break;
        case CalendarEventType.CLINICIAN_APPOINTMENT:
            style.backgroundColor = AXLE_TEAL;
            break;
        case CalendarEventType.CLINICIAN_RESERVATION:
            style.backgroundColor = '#596cf2';
            break;
    }
    return {
        style,
    };
};

export const availabilityScheduleEventPropGetter: EventPropGetter<CalendarEvent> = ({
    type,
}: CalendarEvent) => {
    const style: CSSProperties = {
        borderRadius: '5px',
        color: 'white',
        border: '0px',
        fontSize: '11px',
    };
    switch (type) {
        case CalendarEventType.UNAVAILABILITY:
        case CalendarEventType.SHIFT_BLOCK:
            style.backgroundColor = '#D9D9D9';
            style.opacity = 0.5;
            style.borderRadius = '0';
            style.width = '100%';
            break;
        case CalendarEventType.TENTATIVE_CLINICIAN_APPOINTMENT:
            style.backgroundColor = '#66C1BF';
            style.opacity = 1;
            break;
        case CalendarEventType.CLINICIAN_APPOINTMENT:
            style.backgroundColor = '#3D6B6B';
            style.opacity = 1;
            break;
        default:
            return {};
    }
    return {
        style,
    };
};

/******************************
 * Conversion helper functions
 ******************************/
export const clinicianAppointmentToCalendarEvent = (
    appointment: IClinicianAppointmentScheduleEvent,
): CalendarEvent => {
    const { id, visit, startDatetime, endDatetime, encounters, clinician } = appointment;
    const encounterNames = encounters.map((encounter) => encounter.service.name).join(', ');
    const title = visit?.patient.fullName
        ? `${visit.patient.fullName}-${encounterNames}`
        : encounterNames;
    return {
        type: CalendarEventType.CLINICIAN_APPOINTMENT,
        event: appointment,
        uuid: id,
        title,
        start: startDatetime ? new Date(startDatetime) : new Date(),
        end: endDatetime ? new Date(endDatetime) : new Date(),
        resourceId: clinician?.id ?? '',
    };
};

export const tentativeClinicianAppointmentToCalendarEvent = (
    appointment: ITentativeClinicianAppointmentScheduleEvent,
    result: {
        clinicianId: string;
        tentativeStartDatetime: Date;
    },
): CalendarEvent => {
    const { id, visit, encounters } = appointment;
    const start = moment(result.tentativeStartDatetime).add(
        appointment.startOffsetMinutes,
        'minutes',
    );
    const end = start.clone().add(appointment.durationMinutes, 'minutes');
    return {
        type: CalendarEventType.TENTATIVE_CLINICIAN_APPOINTMENT,
        event: appointment,
        uuid: id,
        title: `${visit.patient.fullName}-${encounters
            .map((encounter) => encounter.service.name)
            .join(', ')}`,
        start: start.toDate(),
        end: end.toDate(),
        resourceId: result.clinicianId,
    };
};

export const shiftToCalendarEvent = (shift: IShiftScheduleEvent): CalendarEvent => {
    const { id, startDatetime, endDatetime, clinician } = shift;
    const start = new Date(startDatetime);
    const end = new Date(endDatetime);
    return {
        type: CalendarEventType.SHIFT,
        event: shift,
        uuid: id,
        title: formatTimeRange(moment(start), moment(end), clinician.timezone),
        start,
        end,
        resourceId: clinician.id,
    };
};

export const shiftBlockToCalendarEvent = (shiftBlock: IShiftBlockScheduleEvent): CalendarEvent => {
    const { id, startDatetime, endDatetime, clinician } = shiftBlock;
    return {
        type: CalendarEventType.SHIFT_BLOCK,
        event: shiftBlock,
        uuid: id,
        title: getShiftBlockOccurrenceLabel(shiftBlock),
        start: new Date(startDatetime),
        end: new Date(endDatetime),
        resourceId: clinician?.id ?? '',
    };
};

export const tentativeShiftBlockToCalendarEvent = (
    tentativeShiftBlock: ITentativeShiftBlockScheduleEvent,
): CalendarEvent => {
    const {
        id,
        startDatetime,
        endDatetime,
        parentRequest: {
            notes,
            requestingClinician: { id: clinicianId },
        },
    } = tentativeShiftBlock;
    return {
        type: CalendarEventType.TENTATIVE_SHIFT_BLOCK,
        event: tentativeShiftBlock,
        uuid: id,
        title: `Tentative: ${notes}`,
        start: startDatetime,
        end: endDatetime,
        resourceId: clinicianId,
    };
};

export const clinicianReservationToCalendarEvent = (
    reservation: IClinicianReservationScheduleEvent,
): CalendarEvent => {
    const { id, clinician, startDatetime, endDatetime } = reservation;
    return {
        type: CalendarEventType.CLINICIAN_RESERVATION,
        event: reservation,
        uuid: id,
        title: `Reservation for ${clinician.firstName} ${clinician.lastName}`,
        start: startDatetime,
        end: endDatetime,
        resourceId: clinician.id,
    };
};

export const unavailabilityToCalendarEvent = (unavailability: {
    id: string;
    startDatetime: Date;
    endDatetime: Date;
    resourceId: string;
}): UnavailabilityCalendarEvent => {
    const { id, startDatetime, endDatetime, resourceId } = unavailability;
    return {
        type: CalendarEventType.UNAVAILABILITY,
        event: unavailability,
        uuid: id,
        title: '',
        start: startDatetime,
        end: endDatetime,
        resourceId,
    };
};
